import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, UserPlus, Ticket, Users, ArrowRight, Check, SkipForward, Loader, Building2, Mail } from "lucide-react";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

const STEPS = [
  { key: "customer", title: "Erster Kunde", icon: UserPlus, desc: "Lege deinen ersten Kunden an — Stammdaten, Tags, Status" },
  { key: "ticket", title: "Erstes Ticket", icon: Ticket, desc: "Erstelle ein Test-Ticket im Kanban-Board" },
  { key: "team", title: "Team einladen", icon: Users, desc: "Lade Kollegen ein (oder überspringe diesen Schritt)" },
];

export default function OnboardingPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState({ customer: false, ticket: false, team: false });
  const [createdCustomer, setCreatedCustomer] = useState(null);

  const markDone = async () => {
    try {
      await api.put("/users/me/onboarded", { onboarded: true });
    } catch {}
    // CRITICAL: update local user state so App.js no longer redirects to /onboarding
    setUser((u) => (u ? { ...u, onboarded: true } : u));
    navigate("/dashboard", { replace: true });
  };

  const finish = () => markDone();
  const skip = () => markDone();

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Toaster richColors position="top-right" />
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-primary" />
          <span className="text-sm font-semibold tracking-tight">Willkommen bei DATON</span>
          <span className="text-xs text-muted-foreground">— {user?.tenant_name}</span>
        </div>
        <button onClick={skip} data-testid="onboarding-skip" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
          <SkipForward size={12} /> Überspringen
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const done = progress[s.key];
              const current = idx === step;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center gap-2 relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      done ? "bg-emerald-500 text-white" :
                      current ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {done ? <Check size={18} /> : <Icon size={16} />}
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${current ? "text-foreground" : "text-muted-foreground"}`}>{s.title}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-colors ${progress[STEPS[idx].key] ? "bg-emerald-500" : "bg-border"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {step === 0 && <StepCustomer onDone={(c) => { setCreatedCustomer(c); setProgress(p => ({ ...p, customer: true })); next(); }} />}
          {step === 1 && <StepTicket customer={createdCustomer} onDone={() => { setProgress(p => ({ ...p, ticket: true })); next(); }} />}
          {step === 2 && <StepTeam onDone={() => { setProgress(p => ({ ...p, team: true })); finish(); }} onSkip={finish} />}
        </div>
      </main>
    </div>
  );
}

function StepCustomer({ onDone }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", email: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/customers", { ...form, status: "active", tags: [] });
      toast.success("Kunde angelegt 🎉");
      onDone(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border shadow-soft p-8 space-y-4 animate-in" data-testid="onboarding-step-customer">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-1">Lege deinen ersten Kunden an</h2>
        <p className="text-sm text-muted-foreground">In 30 Sekunden bist du startklar</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Vorname" required value={form.first_name} onChange={v => setForm({ ...form, first_name: v })} testId="onb-first-name" />
        <Input label="Nachname" required value={form.last_name} onChange={v => setForm({ ...form, last_name: v })} testId="onb-last-name" />
      </div>
      <Input icon={Building2} label="Firma" value={form.company} onChange={v => setForm({ ...form, company: v })} testId="onb-company" />
      <Input icon={Mail} type="email" label="E-Mail" value={form.email} onChange={v => setForm({ ...form, email: v })} testId="onb-customer-email" />
      <button type="submit" disabled={busy} data-testid="onboarding-create-customer"
        className="w-full py-3 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        Kunde anlegen & weiter
      </button>
    </form>
  );
}

function StepTicket({ customer, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/customers/${customer._id}/tickets`, { title: form.title, description: form.description, priority: form.priority, status: "open" });
      toast.success("Ticket angelegt 🎫");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border shadow-soft p-8 space-y-4 animate-in" data-testid="onboarding-step-ticket">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-1">Erstes Ticket erstellen</h2>
        <p className="text-sm text-muted-foreground">Für {customer?.first_name} {customer?.last_name}</p>
      </div>
      <Input label="Titel" required value={form.title} onChange={v => setForm({ ...form, title: v })} placeholder="z. B. Drucker einrichten" testId="onb-ticket-title" />
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Beschreibung</label>
        <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="onb-ticket-desc"
          className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Priorität</label>
        <div className="flex gap-2">
          {[["low", "Niedrig"], ["medium", "Mittel"], ["high", "Hoch"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setForm({ ...form, priority: k })} data-testid={`onb-priority-${k}`}
              className={`flex-1 py-2 text-xs font-semibold border transition-colors ${form.priority === k ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <button type="submit" disabled={busy} data-testid="onboarding-create-ticket"
        className="w-full py-3 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        Ticket erstellen & weiter
      </button>
    </form>
  );
}

function StepTeam({ onDone, onSkip }) {
  const [form, setForm] = useState({ email: "", name: "", role: "technician" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const initials = (form.name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join("") || "MM").toUpperCase();
      await api.post("/users", { ...form, initials, password: "Temp@" + Math.random().toString(36).slice(2, 10) + "!", permissions: { read: true, write: true } });
      toast.success("Kollege eingeladen 📨");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border shadow-soft p-8 space-y-4 animate-in" data-testid="onboarding-step-team">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-1">Team einladen</h2>
        <p className="text-sm text-muted-foreground">Lade einen Kollegen ein, oder überspringe diesen Schritt</p>
      </div>
      <Input label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} testId="onb-team-name" />
      <Input icon={Mail} type="email" label="E-Mail" value={form.email} onChange={v => setForm({ ...form, email: v })} testId="onb-team-email" />
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Rolle</label>
        <div className="flex gap-2">
          {[["technician", "Techniker"], ["admin", "Admin"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setForm({ ...form, role: k })} data-testid={`onb-role-${k}`}
              className={`flex-1 py-2 text-xs font-semibold border transition-colors ${form.role === k ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onSkip} data-testid="onboarding-skip-team" className="flex-1 py-3 border border-border hover:bg-muted text-sm font-semibold">
          Später einladen
        </button>
        <button type="submit" disabled={busy || !form.email || !form.name} data-testid="onboarding-invite-user"
          className="flex-1 py-3 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
          {busy ? <Loader size={15} className="animate-spin" /> : <Check size={15} />}
          Einladen & fertig
        </button>
      </div>
    </form>
  );
}

function Input({ icon: Icon, label, type = "text", required, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </label>
      <input data-testid={testId} type={type} required={required} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none transition-colors" />
    </div>
  );
}
